import { createAction } from '@reduxjs/toolkit'
import type { ICatalogItem, IProduct } from '../interfaces'
// A type-only import: `store.tsx` imports the slices that import this module,
// so a value import would close a runtime cycle.
import type { RootState } from './store'

export interface ISkuSyncingPayload {
  sku: string
  isSyncing: boolean
}

/**
 * Marks the rows of `sku` as syncing while a cart mutation for it is in
 * flight, and unmarks them when the mutation settles. It touches the lists
 * whose slices handle it (see their `extraReducers`), plus the open product:
 * cart items and groups, the catalogue's categories and recommendedProducts,
 * category-nav, filter results, suggested products and the four carousels.
 * The library ItemCard passes a list row's `isSyncing` to its Counter as
 * `disabled`; the `true -> false` edge is what makes the Counter drop a
 * quantity the server refused.
 *
 * Only lists whose quantities `onCartResponse` rewrites belong here, because
 * the edge resyncs the Counter to whatever the row holds. Search results keep
 * the quantity they were loaded with. Linked products are not rewritten by
 * `onCartResponse` either; an effect in `useLinkedProducts` rewrites them, but
 * only while the cart holds a `linkedConditionalStock` line, from a render
 * snapshot and out of step with the settle, so neither the edge nor the clear
 * could be relied on there. Marking either would snap an accepted change back
 * to a stale quantity on the same edge.
 */
export const setSkuSyncing = createAction<ISkuSyncingPayload>('skuSyncing/set')

/** `globals.counter.disableWhileSyncing`, which gates every `setSkuSyncing`. */
export const selectDisableWhileSyncing = (state: RootState) =>
  state.defaultSlice.config?.globals?.counter?.disableWhileSyncing === true

type SyncableRow = Pick<IProduct, 'sku' | 'isSyncing'>

/**
 * Applies the payload to the rows of `sku` in place (an Immer draft). Clearing
 * only touches rows that are still marked, so a row rebuilt from server data
 * mid-flight is not given a field it never had, and rows of other skus are
 * never touched, so concurrent mutations on different skus clear only their
 * own rows.
 *
 * Known limitation: that holds per sku only. Two mutations in flight on the
 * same sku (an add and a delete, say) share one mark, and the first to settle
 * clears it while the other is still in flight.
 */
export const markRowsSyncing = (
  rows: SyncableRow[] | null | undefined,
  { sku, isSyncing }: ISkuSyncingPayload,
) => {
  rows?.forEach((row) => {
    if (row.sku === sku && (isSyncing || row.isSyncing)) {
      row.isSyncing = isSyncing
    }
  })
}

export const markCategoriesSyncing = (
  categories: ICatalogItem[] | null | undefined,
  payload: ISkuSyncingPayload,
) => {
  categories?.forEach(({ productsPrices }) =>
    markRowsSyncing(productsPrices, payload),
  )
}
